/**
 * HouseholdEpisodesTab - Manage case episodes at the household level.
 * Displays on the household detail page as a tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, XCircle, RotateCcw, ChevronDown, ChevronRight,
  Clock, User, CheckCircle2, Archive, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  caseEpisodesAPI, CaseEpisode, EpisodeStatus,
} from '@/services/api/caseEpisodes';

interface HouseholdEpisodesTabProps {
  householdId: number;
  cboId: number;
  programId?: number;
  programs?: Array<{ id: number; name: string; program_type: string }>;
}

const STATUS_COLORS: Record<EpisodeStatus, string> = {
  NEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  SUSPENDED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  COMPLETED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  CLOSED: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  ARCHIVED: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

export default function HouseholdEpisodesTab({
  householdId, cboId, programId, programs = [],
}: HouseholdEpisodesTabProps) {
  const [episodes, setEpisodes] = useState<CaseEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | undefined>(programId);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closingEpisodeId, setClosingEpisodeId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const loadEpisodes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await caseEpisodesAPI.listHouseholdEpisodes(householdId, {
        program_id: programId,
        include_archived: true,
      });
      setEpisodes(data);
    } catch (err) {
      console.error('Failed to load household episodes:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId, programId]);

  useEffect(() => { loadEpisodes(); }, [loadEpisodes]);

  const currentEpisodes = episodes.filter(e => e.is_current);
  const archivedEpisodes = episodes.filter(e => !e.is_current);

  const handleCreate = async () => {
    if (!selectedProgramId) return;
    setActionLoading(true);
    try {
      await caseEpisodesAPI.createHouseholdEpisode({
        cbo_id: cboId,
        household_id: householdId,
        program_id: selectedProgramId,
      });
      setShowCreateDialog(false);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to create household episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = async () => {
    if (!closingEpisodeId || !closeReason.trim()) return;
    setActionLoading(true);
    try {
      await caseEpisodesAPI.closeEpisode(closingEpisodeId, closeReason.trim());
      setCloseDialogOpen(false);
      setCloseReason('');
      setClosingEpisodeId(null);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to close episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async (episodeId: number) => {
    setActionLoading(true);
    try {
      await caseEpisodesAPI.reopenEpisode(episodeId);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to reopen episode:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-sm text-gray-500">Loading episodes...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Household Case Episodes ({episodes.length})
        </h3>
        <Button size="sm" onClick={() => setShowCreateDialog(true)} disabled={actionLoading}>
          <Plus className="w-4 h-4 mr-1" />
          New Episode
        </Button>
      </div>

      {/* Current episodes */}
      {currentEpisodes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-6 text-center">
            <FileText className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-500">No active case episodes for this household</p>
          </CardContent>
        </Card>
      ) : (
        currentEpisodes.map(ep => (
          <Card key={ep.id} className="border-l-4 border-l-blue-500">
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-sm">Case #{ep.case_number}</span>
                    <span className="text-xs text-gray-500">Ep. {ep.episode_number}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ep.status]}`}>
                      {ep.status}
                    </span>
                  </div>
                  {ep.program_name && (
                    <p className="text-xs text-gray-500 mt-1 ml-6">{ep.program_name}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 ml-6 text-xs text-gray-500">
                    <span>Opened: {ep.opened_date || '--'}</span>
                    {ep.assigned_to_name && <span>Assigned: {ep.assigned_to_name}</span>}
                    {ep.payer_name && <span>Plan: {ep.payer_name}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  onClick={() => {
                    setClosingEpisodeId(ep.id);
                    setCloseDialogOpen(true);
                  }}
                  disabled={actionLoading}
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Archived episodes */}
      {archivedEpisodes.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Archived Episodes ({archivedEpisodes.length})
          </button>
          {showArchived && (
            <div className="mt-3 space-y-2">
              {archivedEpisodes.map(ep => (
                <Card key={ep.id} className="bg-gray-50 dark:bg-gray-900/50">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          Case #{ep.case_number}
                          <span className="ml-1.5 text-xs text-gray-500">Ep. {ep.episode_number}</span>
                          {ep.program_name && (
                            <span className="ml-2 text-xs text-gray-400">{ep.program_name}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {ep.opened_date}{ep.closed_date ? ` - ${ep.closed_date}` : ''}
                          {ep.closed_reason && ` (${ep.closed_reason})`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ep.status]}`}>
                          {ep.status}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => handleReopen(ep.id)}
                          disabled={actionLoading}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Reopen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Episode Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Household Episode</DialogTitle>
            <DialogDescription>
              Create a new case episode for this household.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Program</Label>
            {programId ? (
              <p className="text-sm font-medium text-gray-700">
                {programs.find(p => p.id === programId)?.name || `Program #${programId}`}
              </p>
            ) : (
              <Select
                value={selectedProgramId?.toString() || ''}
                onValueChange={(val) => setSelectedProgramId(Number(val))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map(p => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name} ({p.program_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!selectedProgramId || actionLoading}>
              Create Episode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Episode Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Case Episode</DialogTitle>
            <DialogDescription>
              Closing this case will archive it. You can reopen it later if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="hh-close-reason">Reason for closing</Label>
            <Input
              id="hh-close-reason"
              placeholder="e.g., Services completed, household relocated..."
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleClose} disabled={!closeReason.trim() || actionLoading}>
              Close Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
